'use client';

import { imageMetaConverter } from '@lib/server';
import { Place, TypesenseEventDocument } from '@types';
import { parseAsIsoDateTime, useQueryState } from 'nuqs';
import type { Event, WithContext } from 'schema-dts';

/**
 * Strips HTML tags from a string
 */
function stripHtml(html: string | undefined | null): string {
  if (!html) return '';
  return html.replace(/<\/?[^>]+(>|$)/g, '');
}

export function EventSchema({ instances, place }: { instances: TypesenseEventDocument[]; place: Place }) {
  const [date] = useQueryState('date', parseAsIsoDateTime);
  const event = instances.find((instance) => instance.dates === (date?.getTime() || 0) / 1000) || instances[0];

  if (!event) return null;

  const schema: WithContext<Event> = {
    '@context': 'https://schema.org',
    '@type': 'Event',
    name: event.eventName,
    description: stripHtml(event.shortDescription) || '',
    image: imageMetaConverter(event?.image ?? undefined)?.src,
    startDate: new Date(event.dates * 1000).toISOString(),
    endDate: new Date(event.dates_end * 1000).toISOString(),
    eventStatus: event.dates_ticketAvailability === 'cancelled' ? 'EventCancelled' : 'EventScheduled',
    location: {
      '@type': 'Place',
      name: place.name,
      address: [
        place.default?.address?.addressLine1,
        place.default?.address?.addressLine2,
        place.default?.address?.cityOrTown,
        place.default?.address?.county,
        place.default?.address?.country,
        place.default?.address?.postcode,
      ]
        .filter(Boolean)
        .join(', '),
    },
  };

  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />;
}
